import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useBrand } from './BrandContext';
import PublicBooking from './PublicBooking';

function Home() {
  const { user, isMember, isAdmin } = useAuth();
  const brand = useBrand();
  const [searchParams] = useSearchParams();
  const [announcements, setAnnouncements] = useState([]);
  const [upcomingClasses, setUpcomingClasses] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  // Handle redirect parameter - if user is logged in and there's a redirect, go there
  useEffect(() => {
    const redirectUrl = searchParams.get('redirect');
    if (redirectUrl && user) {
      // User is logged in and there's a redirect URL - navigate there
      window.location.href = redirectUrl;
    }
  }, [searchParams, user]);

  useEffect(() => {
    // Load announcements for everyone
    fetch('/api/announcements')
      .then(res => res.json())
      .then(data => {
        if (data.data) setAnnouncements(data.data);
      })
      .catch(() => {});

    setLoading(false);
  }, []);

  useEffect(() => {
    // Load member-specific data
    if (user?.token && isMember) {
      // Load upcoming classes
      fetch('/api/member/upcoming-classes', {
        headers: { 'Authorization': `Bearer ${user.token}` }
      })
        .then(res => res.json())
        .then(data => {
          if (data.data) setUpcomingClasses(data.data);
        })
        .catch(() => {});

      // Load bookings
      fetch('/api/member/my-bookings', {
        headers: { 'Authorization': `Bearer ${user.token}` }
      })
        .then(res => res.json())
        .then(data => {
          if (data.data) setMyBookings(data.data);
        })
        .catch(() => {});
    }
  }, [user, isMember]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  };

  const formatTime = (timeStr) => {
    return timeStr?.substring(0, 5) || '';
  };

  return (
    <div>
      {/* Welcome Section */}
      {!user ? (
        <section className="kp-hero">
          <div className="kp-hero__inner">
            <h1>{brand.hero.title} <em>{brand.hero.titleEm}</em></h1>
            <p>{brand.hero.subtitle}</p>
            <div className="kp-hero__buttons">
              <Link to={brand.hero.ctaPrimary.to} className="btn btn-primary">{brand.hero.ctaPrimary.label}</Link>
              <Link to={brand.hero.ctaSecondary.to} className="btn btn-outline-secondary">{brand.hero.ctaSecondary.label}</Link>
            </div>
          </div>
        </section>
      ) : (
        <div className="mb-4">
          <h2>Hallo, {user.name || user.username}!</h2>
          <p className="text-muted">Willkommen zurueck im Kursplaner.</p>
        </div>
      )}

      {/* Announcements */}
      {announcements.length > 0 && (
        <div className="mb-4">
          <h4>Neuigkeiten</h4>
          {announcements.map(announcement => (
            <div key={announcement.id} className="alert alert-info">
              <strong>{announcement.title}</strong>
              {announcement.content && <p className="mb-0 mt-1">{announcement.content}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Member-specific content */}
      {user && isMember && (
        <div className="row">
          {/* Upcoming Classes */}
          <div className="col-md-6 mb-4">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0">Deine naechsten Termine</h5>
              </div>
              <div className="card-body">
                {upcomingClasses.length > 0 ? (
                  <ul className="list-group list-group-flush">
                    {upcomingClasses.slice(0, 5).map((cls, idx) => (
                      <li key={idx} className="list-group-item d-flex justify-content-between align-items-center px-0">
                        <span
                          className="badge me-2"
                          style={{ backgroundColor: cls.course_color || '#324047' }}
                        >
                          {cls.course_name}
                        </span>
                        <div className="text-end">
                          <div className="fw-bold">{formatDate(cls.date)}</div>
                          <small className="text-muted">{formatTime(cls.time)} Uhr</small>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">Keine anstehenden Termine.</p>
                )}
                {upcomingClasses.length > 0 && (
                  <Link to="/kursplan" className="btn btn-outline-primary btn-sm mt-3">
                    Zum Kursplan
                  </Link>
                )}
              </div>
            </div>
          </div>

          {/* My Bookings */}
          <div className="col-md-6 mb-4">
            <div className="card h-100">
              <div className="card-header">
                <h5 className="mb-0">Meine gebuchten Kurse</h5>
              </div>
              <div className="card-body">
                {myBookings.length > 0 ? (
                  <ul className="list-group list-group-flush">
                    {myBookings.map(booking => (
                      <li
                        key={booking.booking_id}
                        className="list-group-item px-0"
                        style={{ borderLeft: `3px solid ${booking.course_color || '#324047'}` }}
                      >
                        <div className="ms-2">
                          <strong>{booking.course_name}</strong>
                          {booking.course_description && (
                            <p className="text-muted small mb-0 mt-1">
                              {booking.course_description.substring(0, 80)}
                              {booking.course_description.length > 80 && '...'}
                            </p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div>
                    <p className="text-muted mb-2">Du hast noch keine Kurse gebucht.</p>
                    <Link to="/kursplan" className="btn btn-primary btn-sm">
                      Jetzt Kurs buchen
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kennenlernen Section for non-members */}
      {!user && (
        <>
          {brand.routes.schnuppern && (
            <div className="mb-5">
              <PublicBooking />
            </div>
          )}

          {brand.id === 'kids' && (
            <div className="card bg-primary text-white mb-5 border-0 shadow">
              <div className="card-body text-center py-4">
                <h4 className="mb-2 text-white">Werde Mitglied bei Futurhinos for Kids</h4>
                <p className="mb-3">Sichere dir deinen Platz — Schnupper-Pass, Entdecker-Pass, Digital-Pilot oder 10er-Karte.</p>
                <Link to="/mitgliedschaft" className="btn btn-light btn-lg fw-bold">Mitgliedschaft beantragen</Link>
              </div>
            </div>
          )}

          {/* Quick Links — pro Tenant */}
          <div className="row">
            {brand.routes.events && (
              <div className="col-md-4 mb-4">
                <div className="card h-100 hover-lift">
                  <div className="card-body text-center">
                    <h5>Workshops & Events</h5>
                    <p className="text-muted">
                      {brand.id === 'ai'
                        ? 'KI-Workshops und Afterwork-Events fuer Erwachsene'
                        : 'Spannende Workshops fuer Kinder und Erwachsene'}
                    </p>
                    <Link to="/events" className="btn btn-outline-primary btn-sm">Mehr erfahren</Link>
                  </div>
                </div>
              </div>
            )}
            {brand.routes.feriencamps && (
              <div className="col-md-4 mb-4">
                <div className="card h-100 hover-lift">
                  <div className="card-body text-center">
                    <h5>Feriencamps</h5>
                    <p className="text-muted">Spannende Camps in den Schulferien</p>
                    <Link to="/feriencamps" className="btn btn-outline-primary btn-sm">Mehr erfahren</Link>
                  </div>
                </div>
              </div>
            )}
            {brand.routes.geburtstage && (
              <div className="col-md-4 mb-4">
                <div className="card h-100 hover-lift">
                  <div className="card-body text-center">
                    <h5>Kindergeburtstage</h5>
                    <p className="text-muted">Feiere deinen Geburtstag bei uns</p>
                    <Link to="/geburtstage" className="btn btn-outline-primary btn-sm">Mehr erfahren</Link>
                  </div>
                </div>
              </div>
            )}
            {brand.routes.coaching && (
              <div className="col-md-4 mb-4">
                <div className="card h-100 hover-lift">
                  <div className="card-body text-center">
                    <h5>1:1 Coaching</h5>
                    <p className="text-muted">Persoenliches KI-Coaching · vor Ort oder online</p>
                    <Link to="/coaching" className="btn btn-outline-primary btn-sm">Pakete ansehen</Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Admin quick access */}
      {isAdmin && (
        <div className="card mt-4">
          <div className="card-body">
            <h5>Admin-Bereich</h5>
            <Link to="/admin" className="btn btn-secondary">Zum Admin-Bereich</Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;
